import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { IProps } from './types';

const OptInConfirmation = ({ open, onClose, onConfirm, phoneNumber, phoneType }: IProps) => {
  const [agreed, setAgreed] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!open) {
      setAgreed(false);
      setShowError(false);
    }
  }, [open]);

  const handleOptIn = () => {
    if (!agreed) {
      setShowError(true);
      return;
    }
    onConfirm();
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreed(e.target.checked);
    if (e.target.checked) {
      setShowError(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 460,
          borderRadius: 3,
          p: 0,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.1rem',
          pb: 1,
          pt: 2,
          px: 3,
        }}
      >
        Confirm Opt-In
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pb: 2,
          pt: 2,
        }}
      >
        {phoneNumber && phoneType && (
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 600,
              mb: 2,
              mt: 2,
              color: 'text.primary',
            }}
          >
            {phoneNumber} ({phoneType.charAt(0).toUpperCase() + phoneType.slice(1).toLowerCase()})
          </Typography>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              I agree to receive text messages regarding job applications and other opportunities at the phone number provided.
              Msg & data rates may apply. Msg frequency varies. Reply HELP for help and STOP to end.
              For more information view our{' '}
              <a href="https://www.jobtarget.com/terms-of-use" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a> and{' '}
              <a href="https://www.jobtarget.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </Typography>
          }
          sx={{
            alignItems: 'flex-start',
            '& .MuiCheckbox-root': {
              pt: 0,
              mt: '-2px',
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '0.70rem',
              color: 'text.secondary',
              lineHeight: 1.5,
            },
          }}
        />
        {showError && (
          <Typography
            sx={{
              color: 'error.main',
              fontSize: '0.70rem',
              mt: 1,
              ml: 4,
            }}
          >
            You must agree before opting in.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            px: 2.5,
            py: 0.8,
            minWidth: '90px',
            borderColor: '#bdbdbd',
            color: '#424242',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#9e9e9e',
            },
          }}
        >
          Go Back
        </Button>

        <Button
          onClick={handleOptIn}
          variant="contained"
          color="primary"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 2.5,
            py: 0.8,
            minWidth: '90px',
            boxShadow: '0 2px 6px rgba(108, 54, 244, 0.3)',
            transition: 'all 0.25s ease-in-out',
            '&:hover': {
              boxShadow: '0 2px 6px rgba(108, 54, 244, 0.3)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Opt In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptInConfirmation;
